import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import Drawer from '@material-ui/core/Drawer';
import Card from '@material-ui/core/Card';
import { Typography } from '@material-ui/core';
import Close from '@material-ui/icons/Close';
import ArrowBack from '@material-ui/icons/ArrowBack';
import ArrowForward from '@material-ui/icons/ArrowForward';
import { Box, Flex } from '@rebass/grid';
import { useQueryClient } from 'react-query';
import { AppContext } from '../../../app';
import { colors } from '../../../config/theme';
import { useIsMobile } from '../../../lib/hooks/use-is-mobile';
import { useUser } from '../../../lib/hooks/use-user';
import { useBeltSize } from '../../../lib/hooks/use-belt-size';
import { useGymPrograms } from '../../../lib/hooks/use-gym-program';
import { useUserFormEdit } from '../../../lib/hooks/use-user-form-edit';
import { useModal } from '../../../lib/hooks/use-modal';
import ProfileForm from '../../organisim/profile-form';
import AlertDialog from '../../organisim/alert-dialog';
import Profile from './profile';
import { LoadingDots } from '../../atom/loading-dots';

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${colors.onPrimary};
  height: 50px;
  background: ${colors.primary};
  position: sticky;
  top: 0;
  z-index: 1000;
  padding: 0 16px;
  box-sizing: border-box;
`;

const IconWrapper = styled.div`
  cursor: pointer;
`;

const Fade = styled.div`
  animation: fadeIn 0.3s;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const DashboardMemberProfileDrawer = ({ member, open, onClose, onMemberUpdated = () => {}, onSelectRelatedUser }) => {
  const { isMobile } = useIsMobile();
  const { loggedInUserId } = useContext(AppContext);
  const { user: currentUser = {} } = useUser(loggedInUserId);
  const [activeMember, setActiveMember] = useState(member || {});
  const { user: memberCopy = {}, loading: memberLoading } = useUser(activeMember?.uid);
  const [editProfile, setEditProfile] = useState(false);
  const queryClient = useQueryClient();
  const { beltSizes = [] } = useBeltSize(currentUser?.gym?.id);
  const { gymPrograms = [] } = useGymPrograms(currentUser?.gym?.id);
  const {
    isOpen: isFormAlertOpen,
    openModal: openFormAlert,
    closeModal: closeFormAlert,
  } = useModal();

  useEffect(() => {
    if (member?.uid) {
      setActiveMember(member);
      setEditProfile(false);
    }
  }, [member]);

  // Dashboard cards return a lightweight member object. Replace it with the
  // canonical member response as soon as it loads so the editor has the same
  // complete field set as the Gym Members page.
  useEffect(() => {
    if (memberCopy?.uid === activeMember?.uid && !editProfile) {
      setActiveMember(memberCopy);
    }
  }, [activeMember?.uid, editProfile, memberCopy?.uid]);

  const onSubmitCallback = (status) => {
    if (status === 'success') {
      setEditProfile(false);
      queryClient.invalidateQueries('gym-users');
      queryClient.invalidateQueries(['user', activeMember?.uid]);
      onMemberUpdated(activeMember);
    }
  };

  const selectRelatedMember = (relatedMember) => {
    if (onSelectRelatedUser) {
      onSelectRelatedUser(relatedMember);
      return;
    }
    setEditProfile(false);
    setIsFormDirty(false);
    setActiveMember(relatedMember);
  };

  const {
    updateTextField,
    updateSelectField,
    updateErrors,
    uploadImage,
    pendingAvatarFile,
    isAvatarCropOpen,
    confirmAvatarCrop,
    cancelAvatarCrop,
    isUpdatingUser,
    isUploadingImage,
    isFormDirty,
    setIsFormDirty,
    submitForm,
  } = useUserFormEdit(activeMember, setActiveMember, onSubmitCallback, true);

  const requestClose = () => {
    if (editProfile && isFormDirty) {
      openFormAlert();
      return;
    }
    setEditProfile(false);
    onClose();
  };

  return (
    <>
      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={open}
        onClose={requestClose}
        PaperProps={{
          style: {
            height: '100vh',
            width: isMobile ? '100%' : '60%',
            background: colors.surface,
          },
        }}
        transitionDuration={{ enter: 300, exit: 250 }}
        BackdropProps={{ invisible: isMobile }}
      >
        <Header>
          <IconWrapper onClick={requestClose}>
            <Close />
          </IconWrapper>
          <Typography variant="h6">Member Profile</Typography>
          <div />
        </Header>
        <Flex justifyContent="flex-end" pt="20px" style={{ cursor: 'pointer' }}>
          {!editProfile && (
            <Flex onClick={() => setEditProfile(true)} alignItems="center">
              <Box>Edit Profile</Box>
              <Box ml="8px" mr="22px"><ArrowForward /></Box>
            </Flex>
          )}
        </Flex>
        {editProfile && (
          <Flex onClick={() => (isFormDirty ? openFormAlert() : setEditProfile(false))}
            alignItems="center" style={{ cursor: 'pointer' }}>
            <Flex ml="22px" mr="8px"><ArrowBack /></Flex>
            <Flex>Profile</Flex>
          </Flex>
        )}
        {editProfile ? (
          <Fade>
            <Box p="24px">
              <Card style={{ height: '100%', overflow: 'auto', padding: '16px' }}>
                <ProfileForm
                  beltSizes={beltSizes}
                  gymPrograms={gymPrograms}
                  user={activeMember}
                  setState={setActiveMember}
                  updateTextField={updateTextField}
                  updateSelectField={updateSelectField}
                  upDateErrors={updateErrors}
                  uploadImage={uploadImage}
                  pendingAvatarFile={pendingAvatarFile}
                  isAvatarCropOpen={isAvatarCropOpen}
                  confirmAvatarCrop={confirmAvatarCrop}
                  cancelAvatarCrop={cancelAvatarCrop}
                  isUpdatingUser={isUpdatingUser}
                  isImageUploading={isUploadingImage}
                  signup={false}
                  setFormIsDirty={setIsFormDirty}
                  submitForm={submitForm}
                />
              </Card>
            </Box>
          </Fade>
        ) : memberLoading || memberCopy?.uid !== activeMember?.uid ? (
          <Box p="24px"><LoadingDots /></Box>
        ) : (
          <Fade><Profile key={activeMember.uid} user={activeMember} onSelectRelatedUser={selectRelatedMember} /></Fade>
        )}
      </Drawer>
      <AlertDialog
        message="You must save your changes before leaving the edit page. Are you sure you want to leave before saving?"
        title="Are you sure you want to cancel changes?"
        open={isFormDirty && isFormAlertOpen}
        handleCancel={closeFormAlert}
        handleAgree={() => {
          closeFormAlert();
          setActiveMember(memberCopy);
          setIsFormDirty(false);
          setEditProfile(false);
          onClose();
        }}
        buttonCancelText="Cancel"
        buttonAgreeText="Confirm"
      />
    </>
  );
};

export default DashboardMemberProfileDrawer;
